import { TitleBlockTemplate, TitleBlockCell } from "@/app/types/titleblock";

// Simple UUID generator if crypto.randomUUID is not available (though it should be)
function uuidv4(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const DEFAULT_BORDER = { show: true, width: 0.35, color: '#000000' };
const DEFAULT_NEW_CELL_STYLES = {
    borderTop: DEFAULT_BORDER,
    borderRight: DEFAULT_BORDER,
    borderBottom: DEFAULT_BORDER,
    borderLeft: DEFAULT_BORDER
};

export function deepCopyTemplate(template: TitleBlockTemplate): TitleBlockTemplate {
    return JSON.parse(JSON.stringify(template));
}

export function insertRow(template: TitleBlockTemplate, rowIndex: number, height: number = 5): TitleBlockTemplate {
    const t = deepCopyTemplate(template);
    
    // Insert row height
    t.rows.splice(rowIndex, 0, height);
    
    // Shift cells down
    t.cells.forEach(cell => {
        if (cell.rowIndex >= rowIndex) {
            cell.rowIndex++;
        }
        // If a cell spans across the insertion point, increase its span?
        // Logic: If cell starts before rowIndex AND ends after rowIndex
        // (rowIndex is the insertion line index. Row i is inserted AT i.
        // So previous row i becomes i+1.)
        // If cell.rowIndex < rowIndex AND cell.rowIndex + cell.rowSpan > rowIndex:
        // Then we should increase rowSpan.
        else if (cell.rowIndex < rowIndex && (cell.rowIndex + (cell.rowSpan||1)) > rowIndex) {
             cell.rowSpan = (cell.rowSpan || 1) + 1;
        }
    });

    // Fill the new row with empty cells
    const colCount = t.columns.length;
    for (let c = 0; c < colCount; c++) {
        // Check if this spot is already covered by a spanning cell
        const isCovered = t.cells.some(cell => 
            cell.rowIndex < rowIndex && 
            (cell.rowIndex + (cell.rowSpan||1)) > rowIndex &&
            cell.colIndex <= c && 
            (cell.colIndex + (cell.colSpan||1)) > c
        );

        if (!isCovered) {
            t.cells.push({
                id: uuidv4(),
                rowIndex: rowIndex,
                colIndex: c,
                rowSpan: 1,
                colSpan: 1,
                type: 'text',
                value: '',
                styles: { ...t.defaultStyles, ...DEFAULT_NEW_CELL_STYLES }
            });
        }
    }

    return t;
}

export function deleteRow(template: TitleBlockTemplate, rowIndex: number): TitleBlockTemplate {
    const t = deepCopyTemplate(template);
    
    // Remove row height
    t.rows.splice(rowIndex, 1);
    
    // Remove cells in this row
    // But be careful: if a cell spans multiple rows including this one, we should decrease span.
    // If a cell starts at this row, it is deleted.
    
    // We filter out cells that START at this row
    // But what if a cell spans 3 rows and starts here? It's gone. Content lost. Standard behavior.
    t.cells = t.cells.filter(cell => cell.rowIndex !== rowIndex);
    
    // Shift cells up
    t.cells.forEach(cell => {
        if (cell.rowIndex > rowIndex) {
            cell.rowIndex--;
        }
        // If cell spans across the deleted row
        else if (cell.rowIndex < rowIndex && (cell.rowIndex + (cell.rowSpan||1)) > rowIndex) {
            cell.rowSpan = Math.max(1, (cell.rowSpan || 1) - 1);
        }
    });
    
    return t;
}

export function insertCol(template: TitleBlockTemplate, colIndex: number, width: number = 20): TitleBlockTemplate {
    const t = deepCopyTemplate(template);
    t.columns.splice(colIndex, 0, width);
    
    t.cells.forEach(cell => {
        if (cell.colIndex >= colIndex) {
            cell.colIndex++;
        }
        else if (cell.colIndex < colIndex && (cell.colIndex + (cell.colSpan||1)) > colIndex) {
             cell.colSpan = (cell.colSpan || 1) + 1;
        }
    });

    const rowCount = t.rows.length;
    for (let r = 0; r < rowCount; r++) {
        const isCovered = t.cells.some(cell => 
            cell.colIndex < colIndex && 
            (cell.colIndex + (cell.colSpan||1)) > colIndex &&
            cell.rowIndex <= r && 
            (cell.rowIndex + (cell.rowSpan||1)) > r
        );

        if (!isCovered) {
            t.cells.push({
                id: uuidv4(),
                rowIndex: r,
                colIndex: colIndex,
                rowSpan: 1,
                colSpan: 1,
                type: 'text',
                value: '',
                styles: { ...t.defaultStyles, ...DEFAULT_NEW_CELL_STYLES }
            });
        }
    }
    return t;
}

export function deleteCol(template: TitleBlockTemplate, colIndex: number): TitleBlockTemplate {
    const t = deepCopyTemplate(template);
    t.columns.splice(colIndex, 1);
    
    t.cells = t.cells.filter(cell => cell.colIndex !== colIndex);
    
    t.cells.forEach(cell => {
        if (cell.colIndex > colIndex) {
            cell.colIndex--;
        }
        else if (cell.colIndex < colIndex && (cell.colIndex + (cell.colSpan||1)) > colIndex) {
            cell.colSpan = Math.max(1, (cell.colSpan || 1) - 1);
        }
    });
    return t;
}

export function mergeCells(template: TitleBlockTemplate, cellId: string, direction: 'right' | 'down'): TitleBlockTemplate {
    const t = deepCopyTemplate(template);
    const cellIndex = t.cells.findIndex(c => c.id === cellId);
    if (cellIndex === -1) return t;
    
    const cell = t.cells[cellIndex];
    const rSpan = cell.rowSpan || 1;
    const cSpan = cell.colSpan || 1;
    
    // Find target neighbor
    let targetRow = cell.rowIndex;
    let targetCol = cell.colIndex;
    
    if (direction === 'right') {
        targetCol += cSpan; // The column immediately after the current span
    } else {
        targetRow += rSpan; // The row immediately after the current span
    }
    
    // Find the neighbor cell(s) that match the boundary
    // For a clean merge, the neighbor must exactly align.
    // E.g. merging right: neighbor must have same rowIndex and rowSpan (or be a set of cells that fit).
    // Simplification: Only merge if there is a SINGLE neighbor that aligns perfectly in the other dimension.
    
    // Find neighbor starting at targetRow, targetCol
    const neighborIndex = t.cells.findIndex(c => 
        c.rowIndex === targetRow && c.colIndex === targetCol
    );
    
    if (neighborIndex === -1) return t; // No direct neighbor found (maybe empty space or misalignment)
    
    const neighbor = t.cells[neighborIndex];
    const nRSpan = neighbor.rowSpan || 1;
    const nCSpan = neighbor.colSpan || 1;
    
    if (direction === 'right') {
        // Merge Right
        // Constraint: Row positions and heights must match
        if (cell.rowIndex !== neighbor.rowIndex || rSpan !== nRSpan) return t;
        
        // Absorb neighbor
        cell.colSpan = cSpan + nCSpan;
        t.cells.splice(neighborIndex, 1);
    } else {
        // Merge Down
        // Constraint: Col positions and widths must match
        if (cell.colIndex !== neighbor.colIndex || cSpan !== nCSpan) return t;
        
        // Absorb neighbor
        cell.rowSpan = rSpan + nRSpan;
        t.cells.splice(neighborIndex, 1);
    }
    
    return t;
}

export function mergeSelectedCells(template: TitleBlockTemplate, selectedCellIds: string[]): TitleBlockTemplate {
    if (selectedCellIds.length < 2) return template;
    
    const t = deepCopyTemplate(template);
    const selectedCells = t.cells.filter(c => selectedCellIds.includes(c.id));
    
    if (selectedCells.length < 2) return template;

    // 1. Calculate Bounding Box of selection
    let minRow = Infinity;
    let maxRow = -Infinity;
    let minCol = Infinity;
    let maxCol = -Infinity;

    selectedCells.forEach(cell => {
        const rEnd = cell.rowIndex + (cell.rowSpan || 1);
        const cEnd = cell.colIndex + (cell.colSpan || 1);
        
        minRow = Math.min(minRow, cell.rowIndex);
        maxRow = Math.max(maxRow, rEnd);
        minCol = Math.min(minCol, cell.colIndex);
        maxCol = Math.max(maxCol, cEnd);
    });

    // 2. Identify the top-left cell (Master Cell)
    // It should be the cell that starts at minRow, minCol.
    // If no cell starts exactly there (e.g. selection includes partial span), we might have issues.
    // For now, assume user selects a valid block.
    // We'll pick the cell with smallest rowIndex, then smallest colIndex.
    
    selectedCells.sort((a, b) => {
        if (a.rowIndex !== b.rowIndex) return a.rowIndex - b.rowIndex;
        return a.colIndex - b.colIndex;
    });

    const masterCellId = selectedCells[0].id;
    const masterCellIndex = t.cells.findIndex(c => c.id === masterCellId);
    
    if (masterCellIndex === -1) return t;

    const masterCell = t.cells[masterCellIndex];
    
    // 3. Set master cell to cover the entire bounding box
    masterCell.rowIndex = minRow;
    masterCell.colIndex = minCol;
    masterCell.rowSpan = maxRow - minRow;
    masterCell.colSpan = maxCol - minCol;
    
    // 4. Remove all other selected cells
    t.cells = t.cells.filter(c => c.id === masterCellId || !selectedCellIds.includes(c.id));
    
    // 5. Also remove any NON-selected cells that are fully contained within the new bounding box
    // (This handles cases where user selects corners but misses middle cells - controversial but cleaner)
    // Or strictly only remove selected ones. Let's strictly remove selected ones first.
    // BUT: If there are unselected cells INSIDE the bounding box, they will overlap with the master cell.
    // To be safe, we should probably remove ANY cell that is fully inside the bounding box.
    
    t.cells = t.cells.filter(cell => {
        if (cell.id === masterCellId) return true;
        
        // Check if cell is inside the bounding box
        const cellREnd = cell.rowIndex + (cell.rowSpan || 1);
        const cellCEnd = cell.colIndex + (cell.colSpan || 1);
        
        // If cell is completely inside the new master cell area, delete it
        if (cell.rowIndex >= minRow && cellREnd <= maxRow &&
            cell.colIndex >= minCol && cellCEnd <= maxCol) {
            return false;
        }
        return true;
    });
    
    return t;
}


export function splitCell(template: TitleBlockTemplate, cellId: string): TitleBlockTemplate {
    const t = deepCopyTemplate(template);
    const cellIndex = t.cells.findIndex(c => c.id === cellId);
    if (cellIndex === -1) return t;
    
    const cell = t.cells[cellIndex];
    const rSpan = cell.rowSpan || 1;
    const cSpan = cell.colSpan || 1;
    
    if (rSpan === 1 && cSpan === 1) return t; // Nothing to split
    
    // Reset span
    cell.rowSpan = 1;
    cell.colSpan = 1;
    
    // Create new cells for the vacated spots
    // We iterate over the grid covered by the original span
    for (let r = 0; r < rSpan; r++) {
        for (let c = 0; c < cSpan; c++) {
            if (r === 0 && c === 0) continue; // Original cell stays here
            
            const absRow = cell.rowIndex + r;
            const absCol = cell.colIndex + c;
            
            t.cells.push({
                id: uuidv4(),
                rowIndex: absRow,
                colIndex: absCol,
                rowSpan: 1,
                colSpan: 1,
                type: 'text',
                value: '',
                styles: { ...t.defaultStyles, ...DEFAULT_NEW_CELL_STYLES } // Inherit default or maybe original cell styles?
            });
        }
    }
    
    return t;
}

export type BorderMode = 'all' | 'no' | 'outside' | 'thick_outside' | 'top' | 'bottom' | 'left' | 'right' | 'inside' | 'inside_h' | 'inside_v';

export function setBorderMode(
    template: TitleBlockTemplate, 
    cellIds: string[], 
    mode: BorderMode,
    width: number = 0.35
  ): TitleBlockTemplate {
    if (cellIds.length === 0) return template;
    
    const t = deepCopyTemplate(template);
    
    // 1. Calculate Bounding Box of selection to determine "Outside"
    let minRow = Infinity;
    let maxRow = -Infinity;
    let minCol = Infinity;
    let maxCol = -Infinity;
  
    // Filter cells to only those in the template
    const cellsToUpdate = t.cells.filter(c => cellIds.includes(c.id));
    if (cellsToUpdate.length === 0) return t;

    cellsToUpdate.forEach(cell => {
        const rEnd = cell.rowIndex + (cell.rowSpan || 1);
        const cEnd = cell.colIndex + (cell.colSpan || 1);
        
        minRow = Math.min(minRow, cell.rowIndex);
        maxRow = Math.max(maxRow, rEnd);
        minCol = Math.min(minCol, cell.colIndex);
        maxCol = Math.max(maxCol, cEnd);
    });

    const borderOn = { show: true, width, color: '#000000' };
    const borderOff = { show: false, width: 0.25, color: '#000000' }; // Keep default width?
    
    cellsToUpdate.forEach(cell => {
        // Determine position relative to bounding box
        const cellR = cell.rowIndex;
        const cellREnd = cell.rowIndex + (cell.rowSpan || 1);
        const cellC = cell.colIndex;
        const cellCEnd = cell.colIndex + (cell.colSpan || 1);
        
        const isTop = cellR === minRow;
        const isBottom = cellREnd === maxRow;
        const isLeft = cellC === minCol;
        const isRight = cellCEnd === maxCol;
        
        // Helper to ensure styles object exists
        if (!cell.styles) cell.styles = {};
        
        switch (mode) {
            case 'all':
                cell.styles.borderTop = borderOn;
                cell.styles.borderBottom = borderOn;
                cell.styles.borderLeft = borderOn;
                cell.styles.borderRight = borderOn;
                break;
                
            case 'no':
                cell.styles.borderTop = borderOff;
                cell.styles.borderBottom = borderOff;
                cell.styles.borderLeft = borderOff;
                cell.styles.borderRight = borderOff;
                break;
                
            case 'outside':
            case 'thick_outside':
                const outsideBorder = mode === 'thick_outside' ? { ...borderOn, width: 0.7 } : borderOn;
                
                if (isTop) cell.styles.borderTop = outsideBorder;
                if (isBottom) cell.styles.borderBottom = outsideBorder;
                if (isLeft) cell.styles.borderLeft = outsideBorder;
                if (isRight) cell.styles.borderRight = outsideBorder;
                break;
                
            case 'top':
                if (isTop) cell.styles.borderTop = borderOn;
                break;
                
            case 'bottom':
                if (isBottom) cell.styles.borderBottom = borderOn;
                break;
                
            case 'left':
                if (isLeft) cell.styles.borderLeft = borderOn;
                break;
                
            case 'right':
                if (isRight) cell.styles.borderRight = borderOn;
                break;
                
            case 'inside':
            case 'inside_h':
                // Horizontal inside lines: bottom of non-bottom cells, or top of non-top cells
                // Usually we set Bottom for everyone except bottom row
                if (!isBottom) cell.styles.borderBottom = borderOn;
                // And for top row that is NOT top edge? No, just rely on Bottom of previous.
                // But wait, we iterate all cells.
                // If I set Bottom on Row 1, Row 2 Top effectively has a line above it.
                // Do we need to set Top on Row 2?
                // The renderer draws lines independently.
                // Ideally we sync them. But usually just drawing one is enough visually.
                // Let's set Bottom for !isBottom.
                // And if 'inside_v', Right for !isRight.
                break;
        }
        
        if (mode === 'inside_v' || mode === 'inside') {
             if (!isRight) cell.styles.borderRight = borderOn;
        }
    });
    
    return t;
}
